import { useEffect, useRef, useState, useCallback } from 'react';

interface UseFloatingPanelOptions {
  isOpen: boolean;
  anchorId?: string | null;
  inspectorEl: HTMLElement | null;
  width?: number;
  height?: number;
}

interface FloatingPanelPosition {
  top: number;
  left: number;
  isPositioned: boolean;
}

/**
 * Hook to manage floating panel positioning with viewport boundary constraints.
 * Automatically adjusts position to stay within viewport and updates on resize/scroll.
 */
export function useFloatingPanel({
  isOpen,
  anchorId,
  inspectorEl,
  width = 248,
  height = 480,
}: UseFloatingPanelOptions) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const initialTopRef = useRef<number | null>(null);
  const prevAnchorIdRef = useRef<string | null>(null);

  // Reset refs when panel closes or anchor changes
  if (!isOpen || !inspectorEl) {
    initialTopRef.current = null;
    prevAnchorIdRef.current = null;
  } else if (anchorId !== prevAnchorIdRef.current) {
    initialTopRef.current = null;
    prevAnchorIdRef.current = anchorId ?? null;
  }

  // Compute position function - memoized with useCallback
  const computePosition = useCallback(
    (panelEl?: HTMLDivElement | null): FloatingPanelPosition => {
      if (!isOpen || !inspectorEl) {
        return { top: 0, left: 0, isPositioned: false };
      }

      const anchorEl = anchorId ? document.getElementById(anchorId) : null;
      const inspectorRect = inspectorEl.getBoundingClientRect();
      const anchorRect = anchorEl?.getBoundingClientRect();

      // Use actual panel size if available, otherwise use estimates
      const panelWidth = panelEl?.getBoundingClientRect().width || width;
      const panelHeight = panelEl?.getBoundingClientRect().height || height;

      const gap = 8;
      const padding = 8; // Uniform padding on all sides
      const viewportW = window.innerWidth;
      const viewportH = window.innerHeight;

      // Decide left/right based on available space
      const spaceLeft = inspectorRect.left - gap - panelWidth - padding;
      const canPlaceLeft = spaceLeft >= 0;
      const nextLeft = canPlaceLeft
        ? inspectorRect.left - panelWidth - gap
        : inspectorRect.right + gap;

      // Clamp left to ensure panel stays in viewport
      const clampedLeft = Math.max(padding, Math.min(nextLeft, viewportW - panelWidth - padding));

      // Clamp vertically within viewport - but keep initial top locked after first computation
      let clampedTop: number;
      if (initialTopRef.current === null) {
        // First time - compute and store the top position
        const desiredTop = anchorRect ? anchorRect.top : inspectorRect.top;
        const maxTop = Math.max(padding, viewportH - panelHeight - padding);
        clampedTop = Math.min(Math.max(padding, desiredTop), maxTop);
        initialTopRef.current = clampedTop;
      } else {
        // Use the locked initial top position to prevent jumping
        clampedTop = initialTopRef.current;
      }

      return { top: clampedTop, left: clampedLeft, isPositioned: true };
    },
    [isOpen, anchorId, inspectorEl, width, height],
  );

  // Initialize state with computed position
  const [position, setPosition] = useState<FloatingPanelPosition>(() =>
    computePosition(panelRef.current),
  );

  useEffect(() => {
    if (!isOpen || !inspectorEl) {
      return;
    }

    // Setup recompute on DOM paint, resize, scroll, and panel size changes
    const recompute = () => {
      const newPos = computePosition(panelRef.current);
      setPosition(newPos);
    };

    const raf = requestAnimationFrame(recompute);

    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined' && panelRef.current) {
      ro = new ResizeObserver(recompute);
      ro.observe(panelRef.current);
    }

    const onResize = () => recompute();
    const onScroll = () => recompute();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
      ro?.disconnect();
    };
  }, [isOpen, inspectorEl, computePosition]);

  return { position, panelRef };
}
